import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeStyle as _normalizeStyle, withCtx as _withCtx, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-fc8b9ec2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "stake-nominate__header" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "stake-nominate__title" }, "Nominate validators", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-header" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-item" }, "Validator"),
  /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-item" }, "Commission"),
  /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-item" }, "Total stake"),
  /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-item" }, "Estimated returns"),
  /*#__PURE__*/_createElementVNode("div", { class: "stake-nominate__table-item" })
], -1))
const _hoisted_4 = {
  key: 0,
  class: "stake-nominate__loading"
}
const _hoisted_5 = { class: "stake-nominate__count" }
const _hoisted_6 = { class: "stake-nominate__count-all" }
const _hoisted_7 = { class: "stake-nominate__count-select" }

import WhiteWrapper from "@/components/white-wrapper/index.vue";
import ButtonsBlock from "@/components/buttons-block/index.vue";
import BaseButton from "@/components/base-button/index.vue";
import BackButton from "@/components/back-button/index.vue";
import StakeNominateHeaderInfo from "./components/stake-nominate-header-info.vue";
import StakeNominateControls from "./components/stake-nominate-controls.vue";
import StakeNominateItem from "./components/stake-nominate-item.vue";
import CustomScrollbar from "@/components/custom-scrollbar/index.vue";
import SpinnerAnimation from "@/icons/animation/spinner.vue";
import { BaseSelectItem } from "@/types/base-select";
import {
  ComponentPublicInstance,
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import {
  periodOptions,
  periodToNumberOfDays,
  Validator,
} from "@/types/staking";
import { useGetNativeBalances } from "@/libs/balances";
import {
  accounts,
  apiPromise,
  selectedNetwork,
  stakingWizardOptions,
} from "@/stores";
import { getLastEraReward, loadValidatorData } from "@/utils/staking";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props, { expose: __expose }) {

const router = useRouter();

const isHighRisk = ref<boolean>(false);
const isOnlySelected = ref<boolean>(false);

const blockScrollRef = ref<ComponentPublicInstance<HTMLElement>>();
const blockRef = ref<ComponentPublicInstance<HTMLElement>>();
const height = ref<number>(0);
const validators = ref<Array<Validator>>([]);
const selectedValidators = ref<Array<Validator>>([]);

const sortType = ref<number>(0);
const amount = ref<number>(0);
const isCompounding = ref<boolean>(true);
const periodNumberOfDays = ref<number>(365);
const lastEraReward = ref<number>(0);
const isLoading = ref<boolean>(false);

__expose({ blockScrollRef, blockRef });

onMounted(() => {
  window.addEventListener("resize", onResize);
  setTimeout(() => {
    onResize();
  }, 100);

  useGetNativeBalances();
  loadPreviousStakingOptions();
  loadValidators();
});
onUnmounted(() => {
  window.removeEventListener("resize", onResize);
});

watch([selectedNetwork, accounts], () => {
  router.push({ name: "stake-enter-amount" });
});

const onResize = () => {
  if (blockRef.value) {
    const topOffset = (blockRef.value as HTMLElement).getBoundingClientRect()
      .top;
    const windowHeight = window.screen.height;
    const bottom = 24;
    const top = 96;

    height.value = windowHeight - (topOffset + bottom + top);
  }
};

const nextAction = () => {
  stakingWizardOptions.value = {
    ...stakingWizardOptions.value,
    validators: selectedValidators.value,
  };
  router.push({ name: "stake-confirm" });
};

const back = () => {
  router.go(-1);
};

const loadPreviousStakingOptions = () => {
  isCompounding.value = stakingWizardOptions.value.isCompounding;
  const searchPeriod = periodOptions.find(
    (item) => item.id === stakingWizardOptions.value?.period
  );
  if (searchPeriod) {
    periodNumberOfDays.value = periodToNumberOfDays(searchPeriod.id);
  }
  if (
    !stakingWizardOptions.value.amount ||
    !stakingWizardOptions.value.fromAccount
  ) {
    router.replace({
      name: "stake-enter-amount",
    });
    return;
  }
  amount.value = stakingWizardOptions.value.amount;
};

const loadValidators = async () => {
  isLoading.value = true;
  const api = await apiPromise.value;

  lastEraReward.value = await getLastEraReward(api);
  validators.value = await loadValidatorData(api);
  isLoading.value = false;
};

const sortedAndFiltered = computed(() => {
  // Apply filters
  let finalResult = isOnlySelected.value
    ? selectedValidators.value
    : validators.value;

  if (isHighRisk.value) {
    finalResult = finalResult.filter((item) => {
      return !isHighRisk.value || !item.isHighRisk;
    });
  }

  // Apply sorting
  finalResult.sort((a, b) => {
    if (sortType.value === 0) {
      return a.commission - b.commission;
    } else {
      return a.name ? (b.name ? a.name.localeCompare(b.name) : -1) : 1;
    }
  });
  return finalResult;
});

const estimatedYield = computed(() => {
  return isCompounding.value
    ? Math.pow(1 + lastEraReward.value, periodNumberOfDays.value) - 1
    : lastEraReward.value * periodNumberOfDays.value;
});

const estimatedReturn = computed(() => {
  return estimatedYield.value * amount.value;
});

const highRiskSwitch = (isChecked: boolean) => {
  isHighRisk.value = isChecked;
};

const onlySelectedSwitch = (isChecked: boolean) => {
  isOnlySelected.value = isChecked;
};

const selectValidator = (validator: Validator) => {
  selectedValidators.value.push(validator);
};

const deleteValidator = (validator: Validator) => {
  for (var i = selectedValidators.value.length; i--; ) {
    if (selectedValidators.value[i].id === validator.id)
      selectedValidators.value.splice(i, 1);
  }
};

const updateSort = (item: BaseSelectItem) => {
  sortType.value = item.id;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(WhiteWrapper, { class: "stake-nominate__wrap" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(BackButton, { action: back }),
        _hoisted_2,
        _createVNode(StakeNominateHeaderInfo, {
          amount: amount.value,
          returns: estimatedReturn.value,
          yield: estimatedYield.value * 100
        }, null, 8, ["amount", "returns", "yield"])
      ]),
      _createVNode(StakeNominateControls, {
        "is-high-risk": isHighRisk.value,
        "is-only-selected": isOnlySelected.value,
        "onUpdate:highRisk": highRiskSwitch,
        "onUpdate:onlySelected": onlySelectedSwitch,
        "onUpdate:sort": updateSort
      }, null, 8, ["is-high-risk", "is-only-selected"]),
      _hoisted_3,
      _createElementVNode("div", {
        ref_key: "blockRef",
        ref: blockRef,
        class: "stake-nominate__table-block",
        style: _normalizeStyle({ maxHeight: height.value + 'px', minHeight: height.value + 'px' })
      }, [
        _createVNode(CustomScrollbar, {
          ref_key: "blockScrollRef",
          ref: blockScrollRef,
          class: "stake-nominate__scroll-area",
          style: _normalizeStyle({ maxHeight: height.value + 'px' })
        }, {
          default: _withCtx(() => [
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(SpinnerAnimation)
                ]))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(sortedAndFiltered.value, (item, index) => {
                  return (_openBlock(), _createBlock(StakeNominateItem, {
                    key: index,
                    "amount-to-stake": amount.value,
                    validator: item,
                    yield: estimatedYield.value,
                    "onUpdate:selectValidator": selectValidator,
                    "onUpdate:deleteValidator": deleteValidator,
                    "onUpdate:sort": updateSort
                  }, null, 8, ["amount-to-stake", "validator", "yield"]))
                }), 128))
          ]),
          _: 1
        }, 8, ["style"])
      ], 4),
      _createVNode(ButtonsBlock, { "is-border": true }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("p", _hoisted_6, " Choose up to " + _toDisplayString(validators.value.length) + " validators. ", 1),
            _createElementVNode("p", _hoisted_7, _toDisplayString(selectedValidators.value.length) + " selected ", 1)
          ]),
          _createVNode(BaseButton, {
            disabled: selectedValidators.value.length === 0,
            title: "Continue",
            action: nextAction,
            send: true
          }, null, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})